import { Calendar, Phone, Mail } from "lucide-react";
import { BRAND } from "../data/landing";
import { Reveal } from "../components/Reveal";
import { trackCTA } from "../lib/analytics";

const FaxIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 9V3H8v6" />
    <path d="M8 13H6a2 2 0 0 0-2 2v4h4" />
    <rect x="8" y="13" width="13" height="8" rx="1" />
    <path d="M12 17h.01M16 17h.01" />
  </svg>
);

export default function Footer() {
  return (
    <footer
      id="contact"
      className="relative bg-[#050B26] border-t border-white/10 pt-20 sm:pt-28 pb-10"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center mb-14">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#5b80ff] font-semibold mb-4">
            Get in Touch
          </p>
          <h2
            className="font-arial-black text-white leading-tight tracking-tight"
            style={{ fontSize: "clamp(36px, 4.5vw, 60px)" }}
          >
            Let&rsquo;s start the conversation.
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-7">
          <Reveal className="lg:col-span-5 flex flex-col gap-6 lg:gap-7">
            <BookMeetingCard />
            <ContactInfoCard />
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div
              className="contact-card-glow border border-white/15 bg-white/[0.10] backdrop-blur-md p-2.5 sm:p-3 rounded-2xl overflow-hidden"
              data-testid="contact-form-embed"
            >
              <iframe
                src="https://knight-media-group.ninjapipe.app/forms/lead-capture-form-installed-ms102cyy-8062?hidePrivacyNotice=1"
                title="Lead Capture Form (Installed)"
                className="w-full block rounded-lg bg-white"
                style={{ border: "none", height: "1050px" }}
                data-testid="lead-capture-iframe"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </footer>
  );
}

function BookMeetingCard() {
  return (
    <div
      className="contact-card-glow border border-white/15 bg-white/[0.10] backdrop-blur-md p-8 sm:p-10"
      data-testid="book-meeting-card"
    >
      <h3 className="font-arial-black text-white text-2xl sm:text-3xl mb-4">
        Book a Meeting
      </h3>
      <p className="text-white/75 text-sm sm:text-base leading-relaxed mb-7 max-w-sm">
        Ready to get started? Schedule a free strategy session with our team.
      </p>
      <a
        href={BRAND.bookMeetingUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackCTA("Book a Meeting", "footer")}
        className="btn-kmg inline-flex items-center gap-3 bg-[#1440E1] hover:bg-[#1033B3] text-white px-7 py-4 font-bold tracking-wide transition-colors"
        data-testid="footer-book-meeting-btn"
      >
        <Calendar size={18} />
        Book a Meeting
      </a>
    </div>
  );
}

function ContactInfoCard() {
  return (
    <div
      className="contact-card-glow border border-white/15 bg-white/[0.10] backdrop-blur-md p-8 sm:p-10"
      data-testid="contact-information-card"
    >
      <h3 className="font-arial-black text-white text-2xl sm:text-3xl mb-6">
        Contact Information
      </h3>
      <div className="space-y-5">
        <ContactRow icon={Phone} label="Phone" value={BRAND.phone} href={BRAND.phoneHref} tone="brand" testId="footer-contact-phone" />
        <ContactRow icon={FaxIcon} label="Fax" value={BRAND.fax} href={BRAND.faxHref} tone="brand" testId="footer-contact-fax" />
        <ContactRow icon={Mail} label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} tone="brand" testId="footer-contact-email" />
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, tone, testId }) {
  const tile =
    tone === "brand"
      ? "bg-[#1440E1] text-white"
      : "bg-white/10 text-white/80 border border-white/15";
  const Wrap = href ? "a" : "div";
  const isPhoneOrFax = label === "Phone" || label === "Fax";
  return (
    <Wrap href={href} className="flex items-center gap-4 group" data-testid={testId}>
      <span className={`size-11 shrink-0 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105 ${tile}`}>
        <Icon size={18} />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold mb-0.5">
          {label}
        </p>
        <p
          className="text-white text-base sm:text-lg font-semibold leading-snug break-all group-hover:text-[#5b80ff] transition-colors"
          style={isPhoneOrFax ? { fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif' } : undefined}
        >
          {value}
        </p>
      </div>
    </Wrap>
  );
}
